// @ts-nocheck - Large component, minimal TS migration
import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useCreateSupervisorCalendarGoalBookSessionMutation,
  useSendChatMessageMutation,
} from "../../services/apiSlice";
import {
  useLazyFetchSessionByThreadIdQuery,
  firebaseApiSlice,
} from "../../services/firebaseApiSlice";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, ensureNotificationSession } from "../../firebase/firebase";
import ChatContainer from "../../components/ChatContainer/ChatContainer";
import SidebarComponent from "../../components/SidebarComponent/SidebarComponent";
import SearchBarModal from "../../components/SearchBarModal/SearchBarModal";
import UserCrudModal from "../../components/UserCrudModal/UserCrudModal";
import { useAuth, useIsAdmin } from "../../features/auth/authHooks";

export const Main = () => {
  // Get user from Redux
  const { user } = useAuth();
  useIsAdmin();
  const dispatch = useDispatch();

  // RTK Query hooks
  const [sendChatMessage] = useSendChatMessageMutation();
  const [createSupervisorCalendarGoalBookSession] =
    useCreateSupervisorCalendarGoalBookSessionMutation();
  const [fetchSessionByThreadId] = useLazyFetchSessionByThreadIdQuery();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [chatHistory, setChatHistory] = useState({});
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(false);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // const [renameModalVisible, setRenameModalVisible] = useState(false);
  // const [currentRenameChatId, setCurrentRenameChatId] = useState(null);
  // const [newChatName, setNewChatName] = useState("");
  const [showSessions, setShowSessions] = useState(true);
  const [isUserCrudModalOpen, setIsUserCrudModalOpen] = useState(false);
  const [isSearchBarModalOpen, setIsSearchBarModalOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const sessionsListRef = useRef(null);
  const chatHistoryRef = useRef(chatHistory);
  const userRef = useRef(user);
  const loadingSessionRef = useRef(null); // Track currently loading session to prevent duplicates
  const currentChatIdRef = useRef(currentChatId); // Track current chat ID to prevent unnecessary refetches
  const lastNavigationTimeRef = useRef(0); // Track last navigation timestamp to debounce
  const lastSessionUpdateTimeRef = useRef(0); // Track last session update to debounce
  const renderCountRef = useRef(0); // Track render count for debugging
  const messageHandlerCallCountRef = useRef(0); // Track how many times handler is called
  const processedInitialThreadRef = useRef(false); // Avoid duplicate URL thread handling
  // const prevChatKeyRef = useRef(null);
  // const chatHistoryRef = useRef(chatHistory);
  // const userRef = useRef(user);

  // Track renders for debugging (can be removed in production)
  renderCountRef.current += 1;
  if (renderCountRef.current % 10 === 0) {
    console.warn(
      `⚠️ Main component has rendered ${renderCountRef.current} times`,
    );
  }

  // Theme colors
  const colors = {
    light: {
      primary: "#7c4dff",
      secondary: "#b388ff",
      textPrimary: "#333",
      textSecondary: "#666",
      backgroundPrimary: "#fff",
      backgroundSecondary: "#f5f7fb",
      backgroundTertiary: "#edf0f7",
      aiMessageBg: "#f0f4ff",
      userMessageBg: "#7c4dff",
      userMessageText: "#fff",
      borderColor: "#e0e0e0",
      shadowColor: "rgba(0, 0, 0, 0.1)",
    },
    dark: {
      primary: "#9d74ff",
      secondary: "#b388ff",
      textPrimary: "#e0e0e0",
      textSecondary: "#aaaaaa",
      backgroundPrimary: "#1a1a1a",
      backgroundSecondary: "#252525",
      backgroundTertiary: "#333333",
      aiMessageBg: "#2d2d3d",
      userMessageBg: "#9d74ff",
      userMessageText: "#fff",
      borderColor: "#444",
      shadowColor: "rgba(0, 0, 0, 0.6)",
    },
  };

  const theme = isDarkMode ? colors.dark : colors.light;

  // Check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSidebarCollapsed(isMobile);
  }, [isMobile]);

  const cleanMessages = (messages) => {
    if (!messages || messages.length === 0) return [];

    console.log("🧹 Cleaning messages, input count:", messages.length);

    // Step 1: Remove duplicates by ID
    const seenIds = new Set();
    const deduped = messages.filter((msg) => {
      if (!msg) return false;
      if (msg.id && seenIds.has(msg.id)) {
        console.log("🗑️ Removing duplicate by ID:", msg.id);
        return false;
      }
      if (msg.id) seenIds.add(msg.id);
      return true;
    });

    // Step 2: Remove adjacent duplicates
    const withoutAdjacent = [];
    for (let i = 0; i < deduped.length; i++) {
      const current = deduped[i];
      const previous = withoutAdjacent[withoutAdjacent.length - 1];

      // Skip if adjacent duplicate
      if (
        previous &&
        previous.type === current.type &&
        previous.role === current.role &&
        previous.content === current.content &&
        previous.content?.trim()
      ) {
        console.log("🗑️ Removing adjacent duplicate");
        continue;
      }

      withoutAdjacent.push(current);
    }

    // Step 3: Filter out invalid messages
    const cleaned = withoutAdjacent.filter((msg) => {
      // Keep only human and ai messages
      if (msg.type !== "ai" && msg.type !== "human") {
        console.log("🗑️ Removing non-chat message type:", msg.type);
        return false;
      }

      // Remove messages with empty content
      if (!msg.content || !msg.content.trim()) {
        console.log("🗑️ Removing empty message");
        return false;
      }

      return true;
    });

    console.log(
      `✅ Cleaning complete: ${messages.length} -> ${cleaned.length} messages`,
    );
    return cleaned;
  };

  const markSessionHandled = useCallback(
    async (resolvedThreadId, session) => {
      const normalizedAgentType = String(session?.agent_type || "").toLowerCase();
      const isFormAgent =
        normalizedAgentType === "sdsformagent" ||
        normalizedAgentType === "forms_agent" ||
        normalizedAgentType === "formsagent";
      if (!isFormAgent) return;

      const isUnhandled =
        session?.unhandled === true || session?.unhandled === "true";
      if (!isUnhandled) return;

      const normalizedUserEmail =
        (userRef.current?.email || session?.user || "").trim().toLowerCase();
      if (!normalizedUserEmail) return;

      try {
        const sessionDocRef = doc(
          db,
          "sessionsList",
          normalizedUserEmail,
          "sessions",
          resolvedThreadId,
        );
        await updateDoc(sessionDocRef, {
          unhandled: false,
          Progress_status: true,
          last_updated: serverTimestamp(),
        });
        if (sessionsListRef.current?.loadSessions) {
          sessionsListRef.current.loadSessions();
        }
      } catch (error) {
        console.warn("⚠️ Failed to mark session as handled:", error);
      }
    },
    [],
  );

  const loadSession = useCallback((session) => {
    const resolvedThreadId = session.thread_id || session.id;
    if (!resolvedThreadId) {
      console.warn("Missing thread_id/id for session:", session);
      return;
    }

    console.log("📥 Loading session:", resolvedThreadId);

    // Clean messages ONCE using centralized function
    const cleanedMessages = cleanMessages(session.messages || []);

    // Create session chat object
    const sessionChat = {
      id: resolvedThreadId,
      thread_id: resolvedThreadId,
      user: session.user,
      title: session.agent_type || "Session Chat",
      agent_type: session.agent_type,
      date_created: session.date_created,
      messages: cleanedMessages,
      initial_setup: false,
      unhandled: false,
    };

    // Update state in the correct order
    setChatHistory((prev) => ({
      ...prev,
      [resolvedThreadId]: sessionChat,
    }));

    setCurrentChatId(resolvedThreadId);
    setMessages(cleanedMessages); // Use cleaned messages directly
    setPendingFile(null);
    setShowFilePreview(false);
    // Ensure "Needs Response" is cleared once chat is opened.
    markSessionHandled(resolvedThreadId, session);
  }, [markSessionHandled]);

  // Keep refs in sync to avoid stale closures
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Handle initial deep-link thread_id from notification click (cold start)
  useEffect(() => {
    const loadThreadFromQueryParam = async () => {
      if (processedInitialThreadRef.current) return;
      if (!user?.email) return;

      const params = new URLSearchParams(window.location.search);
      const initialThreadId = params.get("thread_id");
      if (!initialThreadId) {
        processedInitialThreadRef.current = true;
        return;
      }

      try {
        const session = await fetchSessionByThreadId({
          userEmail: user.email,
          threadId: initialThreadId,
        }).unwrap();

        if (session) {
          loadSession(session);
          if (isMobile) {
            setIsSidebarCollapsed(true);
          }
        }
      } catch (error) {
        console.error("❌ Error loading initial session from URL:", error);
      } finally {
        processedInitialThreadRef.current = true;
        // Clean URL after handling, so refresh doesn't re-trigger.
        const url = new URL(window.location.href);
        url.searchParams.delete("thread_id");
        window.history.replaceState({}, "", url.toString());
      }
    };

    loadThreadFromQueryParam();
  }, [fetchSessionByThreadId, isMobile, loadSession, user?.email]);

  // UNIFIED message handler for both SESSION_UPDATE and NAVIGATE_TO_CHAT
  // Using useCallback to prevent recreation and infinite loops
  const handleAllMessages = useCallback(
    async (event) => {
      // Track calls for debugging
      messageHandlerCallCountRef.current += 1;
      if (messageHandlerCallCountRef.current % 100 === 0) {
        console.error(
          `🚨 handleAllMessages called ${messageHandlerCallCountRef.current} times! This is a problem.`,
        );
      }

      // Only process messages with proper origin (security check)
      if (event.origin !== window.location.origin && event.origin !== "null") {
        return;
      }

      const messageType = event.data?.type;

      // ===== Handle Create Event form submit from modal =====
      if (messageType === "SUBMIT_CREATE_EVENT_FORM") {
        const threadId = String(event.data?.thread_id || currentChatIdRef.current || "");
        const userEmail = userRef.current?.email;
        const formData = event.data?.formData || {};
        const attendees = Array.isArray(formData.attendees)
          ? formData.attendees
          : String(formData.attendees || "")
              .split(",")
              .map((email) => email.trim())
              .filter(Boolean);

        if (!threadId || !userEmail) return;

        const existingChat = chatHistoryRef.current[threadId] || {};
        const session = {
          ...existingChat,
          id: threadId,
          thread_id: threadId,
          user: userEmail,
          agent_type: "CreateEventAgent",
          event_name: formData.event_name || "",
          start_time: formData.start_time || "",
          end_time: formData.end_time || "",
          scheduling_instructions: formData.scheduling_instructions || "",
          attendees,
          color_id: formData.color_id || "",
          color_id_desc: formData.color_id_desc || "",
          goal_name: formData.goal_name || "",
          goal_for_goal_desc: formData.goal_for_goal_desc || "",
          task_name: formData.task_name || "",
          goal_for_task_desc: formData.goal_for_task_desc || "",
          definition_of_done_for_task: formData.definition_of_done_for_task || "",
          description: formData.description || "",
          location: formData.location || "",
        };
        const submittedEventDetails = {
          event_name: session.event_name,
          start_time: session.start_time,
          end_time: session.end_time,
          scheduling_instructions: session.scheduling_instructions,
          attendees,
          color_id: session.color_id,
          color_id_desc: session.color_id_desc,
          goal_name: session.goal_name,
          goal_for_goal_desc: session.goal_for_goal_desc,
          task_name: session.task_name,
          goal_for_task_desc: session.goal_for_task_desc,
          definition_of_done_for_task: session.definition_of_done_for_task,
          description: session.description,
          location: session.location,
        };
        const submittedEventDetailsContent = [
          "Event details submitted via form.",
          "Use these field values to update the proposed event:",
          JSON.stringify(submittedEventDetails, null, 2),
        ].join("\n");

        const humanMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_human`,
          role: "human",
          type: "human",
          content: "Event details submitted via form.",
        };

        setChatHistory((prev) => {
          const thread = prev[threadId] || {
            id: threadId,
            user: userEmail,
            title: "Create Event",
            agent_type: "CreateEventAgent",
            date_created: Date.now(),
            messages: [],
          };
          return {
            ...prev,
            [threadId]: {
              ...thread,
              messages: [...(thread.messages || []), humanMessage],
            },
          };
        });

        if (currentChatIdRef.current === threadId) {
          setMessages((prev) => [...prev, humanMessage]);
        }

        try {
          setIsTyping(true);
          const aiText = await sendChatMessage({
            userEmail,
            threadId,
            humanInputMessage: { content: submittedEventDetailsContent },
            initialSetup: false,
            session,
          }).unwrap();

          const aiMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_ai`,
            role: "ai",
            type: "ai",
            content: String(aiText || ""),
          };

          setChatHistory((prev) => {
            const thread = prev[threadId] || {
              id: threadId,
              user: userEmail,
              title: "Create Event",
              agent_type: "CreateEventAgent",
              date_created: Date.now(),
              messages: [],
            };
            return {
              ...prev,
              [threadId]: {
                ...thread,
                messages: [...(thread.messages || []), aiMessage],
              },
            };
          });

          if (currentChatIdRef.current === threadId) {
            setMessages((prev) => [...prev, aiMessage]);
          }

          if (sessionsListRef.current?.loadSessions) {
            sessionsListRef.current.loadSessions();
            setTimeout(() => sessionsListRef.current?.loadSessions?.(), 3000);
          }
        } catch (error) {
          console.error("❌ Failed to submit create event form:", error);
        } finally {
          setIsTyping(false);
        }
        return;
      }

      // ===== Handle Goal Set form submit from modal =====
      if (messageType === "SUBMIT_GOALSET_FORM") {
        const threadId = String(event.data?.thread_id || currentChatIdRef.current || "");
        const userEmail = userRef.current?.email;
        const formData = event.data?.formData || {};
        if (!threadId || !userEmail) return;

        const existingChat = chatHistoryRef.current[threadId] || {};
        const session = {
          ...existingChat,
          id: threadId,
          thread_id: threadId,
          user: userEmail,
          agent_type: "GoalSetAgent",
          event_id: formData.event_id || existingChat.event_id || "",
          event_summary: formData.event_summary || existingChat.event_summary || "",
          attendee_email: formData.attendee_email || existingChat.attendee_email || userEmail,
          goal_name: formData.goal_name || "",
          task_name: formData.task_name || "",
          color_id: formData.color_id || "",
          id: formData.id || "",
          desc: formData.desc || "",
          goal_for_goal: formData.goal_for_goal || "",
          goal_for_task: formData.goal_for_task || "",
          definition_of_done: formData.definition_of_done || "",
          count: formData.count || "",
        };

        const goalSetFormPayload = {
          goal_name: session.goal_name,
          task_name: session.task_name,
          color_id: session.color_id,
          id: session.id,
          desc: session.desc,
          goal_for_goal: session.goal_for_goal,
          goal_for_task: session.goal_for_task,
          definition_of_done: session.definition_of_done,
          count: session.count,
          event_id: session.event_id,
          event_summary: session.event_summary,
        };

        const humanMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_human`,
          role: "human",
          type: "human",
          content: "Goal details submitted via form.",
        };

        setChatHistory((prev) => {
          const thread = prev[threadId] || {
            id: threadId,
            user: userEmail,
            title: "Goal Set",
            agent_type: "GoalSetAgent",
            date_created: Date.now(),
            messages: [],
          };
          return {
            ...prev,
            [threadId]: {
              ...thread,
              messages: [...(thread.messages || []), humanMessage],
            },
          };
        });

        if (currentChatIdRef.current === threadId) {
          setMessages((prev) => [...prev, humanMessage]);
        }

        try {
          setIsTyping(true);
          const aiText = await sendChatMessage({
            userEmail,
            threadId,
            humanInputMessage: { content: JSON.stringify(goalSetFormPayload) },
            initialSetup: false,
            session,
          }).unwrap();

          const aiMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_ai`,
            role: "ai",
            type: "ai",
            content: String(aiText || ""),
          };

          setChatHistory((prev) => {
            const thread = prev[threadId] || {
              id: threadId,
              user: userEmail,
              title: "Goal Set",
              agent_type: "GoalSetAgent",
              date_created: Date.now(),
              messages: [],
            };
            return {
              ...prev,
              [threadId]: {
                ...thread,
                messages: [...(thread.messages || []), aiMessage],
              },
            };
          });

          if (currentChatIdRef.current === threadId) {
            setMessages((prev) => [...prev, aiMessage]);
          }

          if (sessionsListRef.current?.loadSessions) {
            sessionsListRef.current.loadSessions();
            setTimeout(() => sessionsListRef.current?.loadSessions?.(), 3000);
          }
        } catch (error) {
          console.error("❌ Failed to submit goal set form:", error);
        } finally {
          setIsTyping(false);
        }
        return;
      }

      // ===== Handle profile updates submit from modal =====
      if (
        messageType === "SUBMIT_CUSTOMER_PROFILE_UPDATES" ||
        messageType === "SUBMIT_PLANMESSAGE_PROFILE_UPDATES"
      ) {
        const threadId = String(event.data?.thread_id || currentChatIdRef.current || "");
        const userEmail = userRef.current?.email;
        const updatedProfiles = Array.isArray(event.data?.updated_profiles)
          ? event.data.updated_profiles
          : [];
        if (!threadId || !userEmail || !updatedProfiles.length) {
          return;
        }

        const existingChat = chatHistoryRef.current[threadId] || {};
        const session = {
          ...existingChat,
          id: threadId,
          thread_id: threadId,
          user: userEmail,
          agent_type: "CustomerProfileAgent",
        };

        const updatedContactLabels = updatedProfiles
          .map((profile) =>
            String(
              (profile as { contact?: string; id?: string })?.contact ||
                (profile as { contact?: string; id?: string })?.id ||
                "Unknown contact",
            ).trim(),
          )
          .filter(Boolean);
        const humanMessageContent =
          updatedContactLabels.length > 0
            ? `Submitted customer profile updates for: ${updatedContactLabels.join(", ")}`
            : "Submitted customer profile updates.";

        const humanMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_human`,
          role: "human",
          type: "human",
          content: humanMessageContent,
        };

        setChatHistory((prev) => {
          const thread = prev[threadId] || {
            id: threadId,
            user: userEmail,
            title: "Customer Profile Agent",
            agent_type: "CustomerProfileAgent",
            date_created: Date.now(),
            messages: [],
          };
          return {
            ...prev,
            [threadId]: {
              ...thread,
              agent_type: "CustomerProfileAgent",
              messages: [...(thread.messages || []), humanMessage],
            },
          };
        });

        if (currentChatIdRef.current === threadId) {
          setMessages((prev) => [...prev, humanMessage]);
        }

        try {
          setIsTyping(true);
          const aiText = await sendChatMessage({
            userEmail,
            threadId,
            humanInputMessage: {
              content: humanMessageContent,
              resume_payload: {
                updated_profiles: updatedProfiles,
              },
            },
            initialSetup: false,
            session,
          }).unwrap();

          const aiMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_ai`,
            role: "ai",
            type: "ai",
            content: String(aiText || ""),
          };

          setChatHistory((prev) => {
            const thread = prev[threadId] || {
              id: threadId,
              user: userEmail,
              title: "Customer Profile Agent",
              agent_type: "CustomerProfileAgent",
              date_created: Date.now(),
              messages: [],
            };
            return {
              ...prev,
              [threadId]: {
                ...thread,
                agent_type: "CustomerProfileAgent",
                messages: [...(thread.messages || []), aiMessage],
              },
            };
          });

          if (currentChatIdRef.current === threadId) {
            setMessages((prev) => [...prev, aiMessage]);
          }

          if (sessionsListRef.current?.loadSessions) {
            sessionsListRef.current.loadSessions();
          }
        } catch (error) {
          console.error("❌ Failed to submit customer profile updates:", error);
        } finally {
          setIsTyping(false);
        }
        return;
      }

      // ===== Handle SESSION_UPDATE =====
      if (messageType === "SESSION_UPDATE") {
        const now = Date.now();
        const timeSinceLastUpdate = now - lastSessionUpdateTimeRef.current;

        if (timeSinceLastUpdate < 5000) {
          console.log(
            "⏭️ Ignoring duplicate session update (debounced within 5s)",
          );
          return;
        }

        console.log("🔄 Session update received, refreshing sessions list");
        lastSessionUpdateTimeRef.current = now;

        if (sessionsListRef.current?.loadSessions) {
          sessionsListRef.current.loadSessions();
        }
        return;
      }

      // ===== Handle NAVIGATE_TO_CHAT =====
      if (messageType === "NAVIGATE_TO_CHAT") {
        const now = Date.now();
        const timeSinceLastNavigation = now - lastNavigationTimeRef.current;

        if (timeSinceLastNavigation < 2000) {
          console.log("⏭️ Ignoring duplicate navigation (debounced within 2s)");
          return;
        }

        console.log("📬 Message received:", {
          type: event.data?.type,
          data: event.data,
          timestamp: new Date().toISOString(),
        });

        const thread_id = event.data?.thread_id;
        const user_email = userRef.current?.email;
        const notificationData =
          event.data?.data && typeof event.data.data === "object"
            ? event.data.data
            : {};

        if (thread_id && user_email) {
          // Check if session already loaded
          if (currentChatIdRef.current === thread_id) {
            console.log("✅ Session already loaded, skipping fetch");
            return;
          }

          // Prevent concurrent fetches
          if (loadingSessionRef.current === thread_id) {
            console.log(
              "⏳ Session already being loaded, skipping duplicate fetch",
            );
            return;
          }

          try {
            console.log(
              "📥 Loading session from notification click:",
              thread_id,
            );
            lastNavigationTimeRef.current = now;
            loadingSessionRef.current = thread_id;
            await ensureNotificationSession({
              userEmail: user_email,
              threadId: thread_id,
              notificationData,
            });

            const session = await fetchSessionByThreadId({
              userEmail: user_email,
              threadId: thread_id,
            }).unwrap();
            if (session) {
              // Use loadSession from ref to avoid dependency issues
              loadSession(session);
              if (isMobile) {
                setIsSidebarCollapsed(true);
              }
            }
          } catch (error) {
            console.error("❌ Error loading session from notification:", error);
          } finally {
            setTimeout(() => {
              loadingSessionRef.current = null;
            }, 500);
          }
        }
      }
    },
    [fetchSessionByThreadId, isMobile, loadSession, sendChatMessage],
  ); // REMOVED loadSession from dependencies - it's causing infinite loop

  // Register the event listener (separate from handler definition)
  useEffect(() => {
    console.log("✅ Registering unified message handler");
    window.addEventListener("message", handleAllMessages);

    return () => {
      console.log("🧹 Cleaning up unified message handler");
      window.removeEventListener("message", handleAllMessages);
    };
  }, [handleAllMessages]); // Re-register when handler changes

  // Separate effect for loading messages when currentChatId or chatHistory changes
  // Using chatHistoryRef to prevent infinite loops from object reference changes
  useEffect(() => {
    const chat = chatHistoryRef.current[currentChatId];
    if (currentChatId && chat) {
      console.log("🔄 Loading messages for chat:", currentChatId);

      const cleanedMessages = cleanMessages(chat.messages || []);

      // Only update if messages actually changed
      setMessages((prevMessages) => {
        const prevIds = prevMessages.map((m) => m.id).join(",");
        const newIds = cleanedMessages.map((m) => m.id).join(",");

        if (prevIds === newIds) {
          console.log("⏭️ Messages unchanged, skipping update");
          return prevMessages;
        }

        console.log("✅ Updating messages:", cleanedMessages.length);
        return cleanedMessages;
      });
    }
  }, [currentChatId]);

  // Initialize the app - create a startup chat once.
  useEffect(() => {
    const initializeStartupChat = () => {
      const date_created = Date.now();
      const thread_id = crypto.randomUUID();
      const initialChat = {
        id: thread_id,
        user: userRef.current?.email,
        title: "New Conversation",
        agent_type: "general",
        date_created,
        messages: [],
        initial_setup: true,
      };

      setChatHistory((prev) => ({
        ...prev,
        [thread_id]: initialChat,
      }));
      setCurrentChatId(thread_id);
      setMessages([]);
      setPendingFile(null);
      setShowFilePreview(false);
    };

    try {
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) {
        setIsDarkMode(savedTheme === "dark");
      }
      initializeStartupChat();
    } catch (error) {
      console.error("Error initializing app:", error);
      initializeStartupChat();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      const scrollContainer = messagesEndRef.current.closest(
        "[data-chat-scroll-container='true']",
      ) as HTMLDivElement | null;
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth",
        });
      } else {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (isTyping) {
      alert("Please wait until the current response is completed.");
      return;
    }

    const message = inputText.trim();
    if (!message && !pendingFile) return;

    // Create new chat if none exists
    if (!currentChatId || !chatHistory[currentChatId]) {
      createNewChat();
      // Wait for state to update before sending
      setTimeout(() => {
        handleSendMessage();
      }, 100);
      return;
    }

    const chatIdAtSend = currentChatId;
    const currentMessages = messages; // Capture current state

    // Create user message with unique ID
    const newUserMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: "human",
      type: "human",
      content: message,
      file: pendingFile
        ? {
            name: pendingFile.name,
            type: pendingFile.type,
            url: pendingFile.url,
          }
        : null,
    };

    // Update messages immediately
    const updatedMessages = [...currentMessages, newUserMessage];
    setMessages(updatedMessages);
    setInputText("");
    setPendingFile(null);
    setShowFilePreview(false);

    // Update chat history
    const updatedChat = {
      ...chatHistory[chatIdAtSend],
      messages: updatedMessages,
    };

    // Set title if first message
    if (updatedMessages.length === 1) {
      const title =
        message.split(" ").slice(0, 4).join(" ") +
        (message.split(" ").length > 4 ? "..." : "");
      updatedChat.title = title;
    }

    setChatHistory((prev) => ({
      ...prev,
      [chatIdAtSend]: updatedChat,
    }));

    // Get AI response
    try {
      setIsTyping(true);
      console.log('the updated chat is', updatedChat);
      const aiResponse = await getAIResponse(updatedChat);

      // Create AI message with unique ID
      const newAiMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: "ai",
        type: "ai",
        content: aiResponse,
      };

      const finalMessages = [...updatedMessages, newAiMessage];

      // Only update if still on the same chat
      if (currentChatIdRef.current === chatIdAtSend) {
        setMessages(finalMessages);
        setChatHistory((prev) => ({
          ...prev,
          [chatIdAtSend]: {
            ...prev[chatIdAtSend],
            messages: finalMessages,
          },
        }));
      }
    } catch (error) {
      console.error("Error getting AI response:", error);
      alert("Sorry, I encountered an error while generating a response.");
    } finally {
      setIsTyping(false);
    }
  };

  // Fix createNewChat to ensure clean state
  const createNewChat = (agentType = "general") => {
    console.log("✨ Creating new chat, agent type:", agentType);

    const date_created = Date.now();
    const thread_id = crypto.randomUUID();

    const newChat = {
      id: thread_id,
      user: user.email,
      title:
        agentType === "CreateEventAgent"
          ? "Crud Supervisor Calendar Agent"
          : agentType === "CustomerProfileAgent"
            ? "Customer Profile Agent"
            : "New Conversation",
      agent_type: agentType,
      date_created: date_created,
      messages: [], // Always start with empty messages
      initial_setup: true,
    };

    setChatHistory((prev) => ({
      ...prev,
      [thread_id]: newChat,
    }));

    setCurrentChatId(thread_id);
    currentChatIdRef.current = thread_id;
    setMessages([]); // Clear messages for new chat
    setPendingFile(null);
    setShowFilePreview(false);

    if (agentType === "CreateEventAgent") {
      createSupervisorCalendarGoalBookSession({
        userEmail: user.email,
        threadId: thread_id,
      })
        .unwrap()
        .then((response) => {
          const markdown = String(response?.message || "").trim();
          if (!markdown) return;

          const aiMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2)}_goal_book`,
            role: "ai",
            type: "ai",
            content: markdown,
          };

          setChatHistory((prev) => {
            const thread = prev[thread_id] || newChat;
            const existingMessages = thread.messages || [];
            const hasGoalBookMessage = existingMessages.some((msg) =>
              String(msg?.id || "").endsWith("_goal_book"),
            );
            return {
              ...prev,
              [thread_id]: {
                ...thread,
                messages: hasGoalBookMessage
                  ? existingMessages
                  : [aiMessage, ...existingMessages],
              },
            };
          });

          if (currentChatIdRef.current === thread_id) {
            setMessages((prev) => {
              const hasGoalBookMessage = prev.some((msg) =>
                String(msg?.id || "").endsWith("_goal_book"),
              );
              return hasGoalBookMessage ? prev : [aiMessage, ...prev];
            });
          }

          if (sessionsListRef.current?.loadSessions) {
            sessionsListRef.current.loadSessions();
          }
        })
        .catch((error) => {
          console.error("Failed to load goal book markdown session:", error);
        });
    }
  };

  // Handle sending a message

  // Simulate AI response
  const getAIResponse = async (chat) => {
    try {
      const initialSetup = chat.messages.length <= 1;
      console.log("initialSetup", initialSetup);
      console.log("Sending chat message to API");

      const aiText = await sendChatMessage({
        userEmail: user.email,
        threadId: chat.id,
        humanInputMessage: chat.messages[chat.messages.length - 1],
        initialSetup,
        session: chat,
      }).unwrap();

      // Refresh sessions list after successful chat message.
      // CreateEventAgent/FollowUpAgent/GoalSetAgent can create or update sessions.
      const isFollowUpAgent = chat.agent_type === "FollowUpAgent";
      const isGoalSetAgent = chat.agent_type === "GoalSetAgent";
      const isCreateEventAgent = chat.agent_type === "CreateEventAgent";
      const isCustomerProfileAgent = chat.agent_type === "CustomerProfileAgent";
      const isPlanMessageAgent = chat.agent_type === "PlanMessageAgent";
      console.log('the agent type is', chat.agent_type, isGoalSetAgent);
      const shouldRefresh =
        isFollowUpAgent ||
        isGoalSetAgent ||
        isCreateEventAgent ||
        isCustomerProfileAgent ||
        isPlanMessageAgent ||
        initialSetup;

      if (shouldRefresh) {
        const refreshReason = isFollowUpAgent
          ? "FollowUpAgent"
          : isGoalSetAgent
            ? "GoalSetAgent"
            : isCreateEventAgent
              ? "CreateEventAgent"
              : isCustomerProfileAgent
                ? "CustomerProfileAgent"
                : isPlanMessageAgent
                  ? "PlanMessageAgent"
                  : "initial setup";
        console.log(
          `🔄 Refreshing sessions list for ${refreshReason} - agent:`,
          chat.agent_type,
        );

        // Invalidate RTK Query cache to force fresh data
        try {
          dispatch(firebaseApiSlice.util.invalidateTags(["Sessions"]));
          console.log("✅ Invalidated Sessions cache tags");
        } catch (err) {
          console.warn("⚠️ Failed to invalidate cache tags:", err);
        }

        const tryReloadSessions = () => {
          if (
            sessionsListRef.current &&
            sessionsListRef.current.loadSessions
          ) {
            console.log("🔄 Calling loadSessions()");
            sessionsListRef.current.loadSessions();
          } else {
            console.warn(
              "⚠️ sessionsListRef.current.loadSessions is not available",
            );
          }
        };

        tryReloadSessions();
        setTimeout(tryReloadSessions, 2000);
        if (isCreateEventAgent) {
          setTimeout(tryReloadSessions, 5000);
        }
      } else {
        console.log(
          "⏭️ Skipping session refresh - not a session-updating agent and not initial setup",
        );
      }

      return aiText;
    } catch (error) {
      console.error("Error getting AI response:", error);
      // Fallback to mock response if API fails
      const responses = [
        "I'm an AI assistant here to help you. How can I assist you today?",
        "That's an interesting question. Let me think about that...",
        "Here's what I found about your query.",
        "I can help with that. Here's some information that might be useful.",
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setPendingFile({
        name: file.name,
        type: file.type,
        url: fileUrl,
      });
      setShowFilePreview(true);
    }
  };

  // Clear chat history (in-memory only)
  const clearAllHistory = () => {
    if (
      window.confirm(
        "Are you sure you want to clear current chat history? This cannot be undone.",
      )
    ) {
      console.log("Clearing chat history...");
      // localStorage.removeItem("chatHistory");
      // Reset all chat-related state
      setChatHistory({});
      setMessages([]);
      setCurrentChatId(null);
      // Create a fresh chat
      createNewChat();
    }
  };

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Export current chat
  const exportCurrentChat = () => {
    if (!currentChatId || !chatHistory[currentChatId]) return;

    const chat = chatHistory[currentChatId];
    let exportText = `# ${chat.title}\n\n`;

    chat.messages.forEach((message) => {
      const role = message.role === "user" ? "You" : "AI";
      exportText += `## ${role}:\n${message.content}\n\n`;
    });

    const blob = new Blob([exportText], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${chat.title.replace(/[^\w\s]/gi, "")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  console.log("[Main] Component rendering:", {
    hasUser: !!user,
    userEmail: user?.email,
  });

  // Don't render if user is not available (shouldn't happen if auth check works, but safety first)
  if (!user || !user.email) {
    console.log("[Main] User not available, showing loading screen");
    return (
      <div
        className="h-screen flex items-center justify-center"
        style={{ backgroundColor: "#f5f7fb", color: "#333" }}
      >
        <div>Loading user data...</div>
      </div>
    );
  }

  console.log("[Main] User available, rendering main content");

  return (
    <div
      className="h-screen box-border overflow-hidden flex"
      style={{
        backgroundColor: theme.backgroundSecondary,
        padding: isMobile ? "10px" : "20px",
      }}
    >
      {/* Dropdown Button - Fixed position */}

      {/* User CRUD Modal */}
      <UserCrudModal
        isOpen={isUserCrudModalOpen}
        onClose={() => setIsUserCrudModalOpen(false)}
        theme={theme}
      />

      {/* Search Bar Modal */}
      <SearchBarModal
        isOpen={isSearchBarModalOpen}
        onClose={() => setIsSearchBarModalOpen(false)}
        theme={theme}
      />

      {/* Rename Modal */}
      {/* {renameModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="p-6 rounded-lg w-96"
            style={{ backgroundColor: theme.backgroundPrimary }}
          >
            <h3
              className="text-lg font-semibold mb-4"
              style={{ color: theme.textPrimary }}
            >
              Rename Chat
            </h3>
            <input
              type="text"
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              className="w-full p-2 border rounded mb-4 outline-none"
              style={{
                backgroundColor: theme.backgroundTertiary,
                color: theme.textPrimary,
                borderColor: theme.borderColor,
              }}
              placeholder="Enter new chat name"
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setRenameModalVisible(false)}
                className="px-4 py-2 rounded hover:bg-gray-100"
                style={{ color: theme.textPrimary }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameConfirm}
                className="px-4 py-2 rounded text-white"
                style={{ backgroundColor: theme.primary }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )} */}
      {/* Main App Container */}
      <div
        className="flex-1 min-h-0 flex rounded-lg overflow-hidden"
        style={{ backgroundColor: theme.backgroundPrimary }}
      >
        {/* Sidebar */}
        <SidebarComponent 
          ref={sessionsListRef}
          theme={theme}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          isMobile={isMobile}
          user={user}
          // chatHistory={chatHistory}
          // currentChatId={currentChatId}
          // loadChat={loadChat}
          loadSession={loadSession}
          createNewChat={createNewChat}
          // deleteChat={deleteChat}
          // renameChat={renameChat}
          clearAllHistory={clearAllHistory}
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          showSessions={showSessions}
          setShowSessions={setShowSessions}
        />

        {/* Chat Container */}
        <ChatContainer
          theme={theme}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          currentChatId={currentChatId}
          chatHistory={chatHistory}
          exportCurrentChat={exportCurrentChat}
          isTyping={isTyping}
          messages={messages}
          user={user}
          messagesEndRef={messagesEndRef}
          showFilePreview={showFilePreview}
          pendingFile={pendingFile}
          setPendingFile={setPendingFile}
          setShowFilePreview={setShowFilePreview}
          fileInputRef={fileInputRef}
          handleFileUpload={handleFileUpload}
          inputText={inputText}
          setInputText={setInputText}
          handleSendMessage={handleSendMessage}
        />
      </div>

      {/* Blog Studio floating button */}
      <Link
        to="/blog"
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 18px",
          borderRadius: "50px",
          backgroundColor: "#7c4dff",
          color: "#fff",
          textDecoration: "none",
          fontSize: "14px",
          fontWeight: "bold",
          boxShadow: "0 4px 16px rgba(124,77,255,0.4)",
          zIndex: 1000,
          transition: "transform 0.15s, box-shadow 0.15s",
        }}
        title="Open Blog Studio"
      >
        ✍️ Blog Studio
      </Link>
    </div>
  );
};
